<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import { usePdf } from "#/composables/usePdfMode";
import { useMainStore } from "../../store/main";

const props = defineProps<{
	axis: "x" | "y";
	behavior: "auto" | "smooth";
	autoScroll: boolean;
	container: HTMLElement | string | null;
	idActive?: string | null;
	clearContainer: boolean;
}>();

const mainStore = useMainStore();
const wrapperRef = ref<HTMLElement | null>(null);
const { isDesktop } = usePdf();
const scrollToElement = async (targetElement: HTMLElement) => {
	if (!wrapperRef.value || !targetElement) return;

	let scrollContainer: Element | Window = window;
	if (props.container) {
		scrollContainer = typeof props.container === "string" ? document.querySelector(props.container) || window : props.container;
	}

	const options: ScrollToOptions = {
		behavior: props.behavior,
	};

	if (props.axis === "x") {
		const containerWidth = scrollContainer === window ? window.innerWidth : (scrollContainer as HTMLElement).clientWidth;
		const elementWidth = targetElement.offsetWidth;
		let scrollLeft = targetElement.offsetLeft;

		if (scrollContainer !== window) {
			const containerRect = (scrollContainer as HTMLElement).getBoundingClientRect();
			const elementRect = targetElement.getBoundingClientRect();
			scrollLeft = elementRect.left - containerRect.left + (scrollContainer as HTMLElement).scrollLeft;
		}

		scrollLeft -= (containerWidth - elementWidth) / 2;
		options.left = Math.max(0, scrollLeft);
	} else {
		const containerHeight = scrollContainer === window ? window.innerHeight : (scrollContainer as HTMLElement).clientHeight;
		const elementHeight = targetElement.offsetHeight;
		let scrollTop = targetElement.offsetTop;

		if (scrollContainer !== window) {
			const containerRect = (scrollContainer as HTMLElement).getBoundingClientRect();
			const elementRect = targetElement.getBoundingClientRect();
			scrollTop = elementRect.top - containerRect.top + (scrollContainer as HTMLElement).scrollTop;
		}

		scrollTop -= (containerHeight - elementHeight) / 2;
		options.top = Math.max(0, scrollTop);
	}

	try {
		if (scrollContainer === window) {
			window.scrollTo(options);
		} else {
			(scrollContainer as HTMLElement).scrollTo(options);
		}
	} catch (error) {
		console.error("Scroll error:", error);
	}
};

onMounted(async () => {
	if (props.autoScroll) {
		let targetElement: HTMLElement | null = null;
		await nextTick();

		if (mainStore.activeScrolling_lib) {
			const indexElement = mainStore.activeScrolling_lib.findIndex((item: HTMLElement) => item.id === props.idActive);

			if (props.idActive) {
				targetElement = document.getElementById(`scrolling${props.idActive}`);
				if (targetElement && indexElement !== -1) {
					mainStore.activeScrolling_lib[indexElement] = targetElement;
				}
			} else if (mainStore.activeScrolling_lib[indexElement]) {
				targetElement = mainStore.activeScrolling_lib[indexElement];
			}
			scrollToElement(targetElement as HTMLElement);
		}
	}
});

const handleTargetClick = async (event: Event) => {
	if (props.clearContainer) {
		mainStore.activeScrolling_lib = [];
	}
	const target = event.currentTarget as HTMLElement;
	await nextTick();
	if (!mainStore.activeScrolling_lib.includes(target)) {
		mainStore.activeScrolling_lib.push(target);
	}

	scrollToElement(target);
};

defineExpose({
	scrollToElement,
});
</script>

<template>
	<div ref="wrapperRef" class="scroll-to-wrapper" :class="{ scroll_pdf: isDesktop }">
		<slot :handleTargetClick="handleTargetClick" />
	</div>
</template>

<style scoped lang="scss">
  @use './useScrollCenter.scss';
</style>
